import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2, Search, Pencil, LogOut, Package, FileText, Upload, Image as ImageIcon, Download, FileSpreadsheet, Trash2, Phone, ArrowUpDown, ArrowUp, ArrowDown, X, Video } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatPrice = (p) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(p);

function getAuthHeaders() {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) { navigate("/admin/login"); return; }
    axios.get(`${API}/auth/me`, { headers: getAuthHeaders() })
      .then(({ data }) => { setUser(data); setLoading(false); })
      .catch(() => { localStorage.removeItem("admin_token"); navigate("/admin/login"); });
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/admin/login");
  };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-brand-primary" /></div>;

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-neutral-900" data-testid="admin-title">Admin Dashboard</h1>
          <p className="text-sm text-neutral-500">{user?.email}</p>
        </div>
        <Button variant="outline" onClick={handleLogout} data-testid="admin-logout-btn"><LogOut className="h-4 w-4 mr-2" /> Logout</Button>
      </div>

      <Tabs defaultValue="products">
        <TabsList className="mb-6">
          <TabsTrigger value="products" data-testid="tab-products"><Package className="h-4 w-4 mr-2" /> Products</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories"><ImageIcon className="h-4 w-4 mr-2" /> Categories</TabsTrigger>
          <TabsTrigger value="rfqs" data-testid="tab-rfqs"><FileText className="h-4 w-4 mr-2" /> RFQs</TabsTrigger>
        </TabsList>
        <TabsContent value="products"><ProductsTab /></TabsContent>
        <TabsContent value="categories"><CategoriesTab /></TabsContent>
        <TabsContent value="rfqs"><RFQsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function SortableHeader({ label, sortKey, currentSort, onSort, className = "" }) {
  const isActive = currentSort?.startsWith(sortKey);
  const isAsc = currentSort === `${sortKey}_asc`;
  const isDesc = currentSort === `${sortKey}_desc`;

  const handleClick = () => {
    if (!isActive || isDesc) onSort(`${sortKey}_asc`);
    else if (isAsc) onSort(`${sortKey}_desc`);
  };

  return (
    <TableHead className={`font-semibold cursor-pointer select-none hover:bg-neutral-100 transition-colors ${className}`} onClick={handleClick}>
      <div className={`flex items-center gap-1 ${className.includes('text-right') ? 'justify-end' : ''}`}>
        {label}
        {isAsc ? <ArrowUp className="h-3 w-3 text-brand-primary" /> : isDesc ? <ArrowDown className="h-3 w-3 text-brand-primary" /> : <ArrowUpDown className="h-3 w-3 text-neutral-300" />}
      </div>
    </TableHead>
  );
}

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState("");
  const [editProduct, setEditProduct] = useState(null);
  const [deleteProduct, setDeleteProduct] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sortBy, setSortBy] = useState("name_asc");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("");
  const [categories, setCategories] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const fileInputRef = useRef(null);
  const sentinelRef = useRef(null);
  const pageRef = useRef(1);
  const LIMIT = 50;

  useEffect(() => {
    axios.get(`${API}/categories`).then(({ data }) => {
      const names = (Array.isArray(data) ? data : []).map(c => c.name).filter(Boolean).sort();
      setCategories(names);
    }).catch(() => {});
  }, []);

  const fetchProducts = useCallback(async (reset = false) => {
    if (reset) {
      setLoading(true);
      pageRef.current = 1;
    } else {
      setLoadingMore(true);
    }
    try {
      const params = { page: pageRef.current, limit: LIMIT, sort: sortBy };
      if (search) params.search = search;
      if (categoryFilter) params.category = categoryFilter;
      if (stockFilter) params.in_stock = stockFilter;
      const { data } = await axios.get(`${API}/products`, { params });
      if (reset) {
        setProducts(data.products);
      } else {
        setProducts(prev => [...prev, ...data.products]);
      }
      setTotalCount(data.total);
      setHasMore(pageRef.current < data.total_pages);
    } catch (err) { toast.error("Failed to load products"); }
    finally { setLoading(false); setLoadingMore(false); }
  }, [search, sortBy, categoryFilter, stockFilter]);

  useEffect(() => { fetchProducts(true); }, [fetchProducts]);

  useEffect(() => {
    if (!sentinelRef.current || loading) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMore && !loadingMore) {
        pageRef.current += 1;
        fetchProducts(false);
      }
    }, { rootMargin: '200px' });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, fetchProducts]);

  const handleSort = (newSort) => { setSortBy(newSort); };

  const hasFilters = categoryFilter || stockFilter;
  const clearFilters = () => { setCategoryFilter(""); setStockFilter(""); };

  const handleSave = async (id, updates) => {
    try {
      await axios.put(`${API}/products/${id}`, updates, { headers: getAuthHeaders() });
      toast.success("Product updated");
      setEditProduct(null);
      fetchProducts();
    } catch (err) { toast.error("Update failed"); }
  };

  const handleDelete = async (permanent = false) => {
    if (!deleteProduct) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/products/${deleteProduct.id}?permanent=${permanent}`, { headers: getAuthHeaders() });
      toast.success(permanent ? "Product permanently deleted" : "Product soft deleted");
      setDeleteProduct(null);
      fetchProducts();
    } catch (err) { toast.error("Delete failed"); }
    finally { setDeleting(false); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/products/bulk-export`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'products_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Products exported successfully");
    } catch (err) {
      console.error("Export error:", err);
      const errorMessage = err.response?.data?.detail || err.message || "Export failed";
      toast.error(errorMessage);
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await axios.post(`${API}/products/bulk-import`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Import completed: ${data.created} created, ${data.updated} updated`);
      if (data.errors.length > 0) {
        toast.warning(`${data.errors.length} errors occurred. Check console for details.`);
        console.error("Import errors:", data.errors);
      }
      fetchProducts();
    } catch (err) {
      toast.error("Import failed");
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <Input data-testid="admin-product-search" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" />
        </div>
        <Select value={categoryFilter} onValueChange={(val) => setCategoryFilter(val === "all" ? "" : val)}>
          <SelectTrigger className="w-[180px] h-9">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={stockFilter} onValueChange={(val) => setStockFilter(val === "all" ? "" : val)}>
          <SelectTrigger className="w-[140px] h-9">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="true">In Stock</SelectItem>
            <SelectItem value="false">Out of Stock</SelectItem>
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-neutral-500 hover:text-neutral-700">
            <X className="h-4 w-4 mr-1" />Clear
          </Button>
        )}
        <div className="flex-1" />
        <Button variant="outline" onClick={handleExport} disabled={exporting}>
          {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
          Export
        </Button>
        <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={importing}>
          {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
          Import
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImport}
          accept=".csv,.xlsx,.xls"
          className="hidden"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand-primary" /></div>
      ) : (
        <>
          <div className="border rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-neutral-50">
                  <SortableHeader label="Code" sortKey="code" currentSort={sortBy} onSort={handleSort} />
                  <SortableHeader label="Product" sortKey="name" currentSort={sortBy} onSort={handleSort} />
                  <SortableHeader label="Category" sortKey="category" currentSort={sortBy} onSort={handleSort} />
                  <SortableHeader label="Price" sortKey="price" currentSort={sortBy} onSort={handleSort} className="text-right" />
                  <SortableHeader label="Stock" sortKey="stock" currentSort={sortBy} onSort={handleSort} className="text-right" />
                  <TableHead className="font-semibold text-center">Status</TableHead>
                  <TableHead className="font-semibold text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map(p => (
                  <TableRow key={p.id} data-testid={`admin-product-row-${p.item_code}`}>
                    <TableCell className="font-mono text-xs text-neutral-500">{p.item_code}</TableCell>
                    <TableCell className="font-medium text-sm max-w-[200px] truncate">{p.name}</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">{p.category}</Badge></TableCell>
                    <TableCell className="text-right font-medium">{formatPrice(p.price)}</TableCell>
                    <TableCell className="text-right">{p.stock}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant={p.in_stock ? "default" : "destructive"} className={p.in_stock ? "bg-emerald-100 text-emerald-700" : ""}>
                        {p.in_stock ? "In Stock" : "Out"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setEditProduct(p)} data-testid={`edit-btn-${p.item_code}`}><Pencil className="h-3 w-3" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteProduct(p)} data-testid={`delete-btn-${p.item_code}`}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div ref={sentinelRef} className="h-1" />
          {loadingMore && (
            <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-brand-primary" /></div>
          )}
          <div className="text-center text-xs text-neutral-400 py-2">
            Showing {products.length} of {totalCount} products
          </div>
        </>
      )}

      {editProduct && <EditProductDialog product={editProduct} onClose={() => setEditProduct(null)} onSave={handleSave} />}
      {deleteProduct && (
        <Dialog open onOpenChange={() => setDeleteProduct(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Product</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-neutral-600">
                Are you sure you want to delete <strong>{deleteProduct.name}</strong>?
              </p>
              <div className="mt-4 space-y-2">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleDelete(false)}
                  disabled={deleting}
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Soft Delete (Can be restored)
                </Button>
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => handleDelete(true)}
                  disabled={deleting}
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Permanent Delete (Cannot be restored)
                </Button>
              </div>
              <p className="text-xs text-neutral-500 mt-4">
                <strong>Soft Delete:</strong> Marks product as deleted but keeps it in database. Can be restored by setting deleted=false in import.
                <br />
                <strong>Permanent Delete:</strong> Removes product from database completely.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteProduct(null)}>Cancel</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function EditProductDialog({ product, onClose, onSave }) {
  const [price, setPrice] = useState(product.price);
  const [stock, setStock] = useState(product.stock);
  const [inStock, setInStock] = useState(product.in_stock);
  const [name, setName] = useState(product.name);
  const [imageUrl, setImageUrl] = useState(product.image_url || "");
  const [images, setImages] = useState(product.images || []);
  const [videos, setVideos] = useState(product.videos || []);
  const [newImageUrl, setNewImageUrl] = useState("");
  const [newVideoUrl, setNewVideoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const imagesInputRef = useRef(null);
  const videosInputRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await axios.post(`${API}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImageUrl(data.image_url);
      toast.success("Image uploaded successfully");
    } catch (err) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleMultipleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const { data } = await axios.post(`${API}/upload-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploadedUrls.push(data.image_url);
      } catch (err) {
        console.error("Failed to upload image:", err);
      }
    }

    setImages([...images, ...uploadedUrls]);
    toast.success(`${uploadedUrls.length} images uploaded successfully`);
    setUploading(false);
    if (imagesInputRef.current) {
      imagesInputRef.current.value = '';
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await axios.post(`${API}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setVideos([...videos, data.image_url]);
      toast.success("Video uploaded successfully");
    } catch (err) {
      toast.error("Failed to upload video");
    } finally {
      setUploading(false);
      if (videosInputRef.current) {
        videosInputRef.current.value = '';
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleRemoveVideo = (index) => {
    setVideos(videos.filter((_, i) => i !== index));
  };

  const handleAddImageUrl = () => {
    const u = newImageUrl.trim();
    if (!u) return;
    setImages([...images, u]);
    setNewImageUrl("");
    toast.success("Image link added");
  };

  const handleAddVideoUrl = () => {
    const u = newVideoUrl.trim();
    if (!u) return;
    setVideos([...videos, u]);
    setNewVideoUrl("");
    toast.success("Video link added");
  };

  const handleSubmit = async () => {
    setSaving(true);
    await onSave(product.id, { 
      price: Number(price), 
      stock: Number(stock), 
      in_stock: inStock, 
      name, 
      image_url: imageUrl,
      images,
      videos
    });
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="edit-product-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit Product</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto">
          <div>
            <Label className="text-sm font-medium">Product Image</Label>
            <div className="mt-2 space-y-2">
              {imageUrl && (
                <img src={imageUrl} alt="Product" className="w-32 h-32 object-contain border rounded-lg bg-neutral-50" />
              )}
              <div className="flex gap-2">
                <Input
                  data-testid="image-url-input"
                  placeholder="Enter image URL or upload file"
                  value={imageUrl}
                  onChange={e => setImageUrl(e.target.value)}
                  className="flex-1"
                />
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={uploading}
                  ref={fileInputRef}
                />
                <Button type="button" variant="outline" size="icon" onClick={handleUploadClick} disabled={uploading}>
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-neutral-500">Upload image from local or paste URL from Drive/Cloud</p>
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium">Additional Images</Label>
            <div className="mt-2 space-y-2">
              {images.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {images.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img src={img} alt={`Image ${idx + 1}`} className="w-16 h-16 object-cover rounded-lg border" />
                      <button
                        onClick={() => handleRemoveImage(idx)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleMultipleImageUpload}
                  className="hidden"
                  disabled={uploading}
                  ref={imagesInputRef}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => imagesInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                  Upload Multiple Images
                </Button>
              </div>
              <div className="flex gap-2">
                <Input
                  data-testid="add-image-url-input"
                  placeholder="Paste image URL (postimg, Drive, etc.)"
                  value={newImageUrl}
                  onChange={e => setNewImageUrl(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddImageUrl(); } }}
                  className="flex-1"
                />
                <Button type="button" variant="outline" size="sm" onClick={handleAddImageUrl}>Add</Button>
              </div>
              <p className="text-xs text-neutral-500">Paste a direct image link and click Add. Links are more reliable than uploaded files on the current host.</p>
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium">Videos</Label>
            <div className="mt-2 space-y-2">
              {videos.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {videos.map((vid, idx) => (
                    <div key={idx} className="relative group">
                      <div className="w-16 h-16 rounded-lg border bg-neutral-100 flex flex-col items-center justify-center" title={vid}>
                        <Video className="h-5 w-5 text-neutral-500" />
                        <span className="text-[8px] text-neutral-400 mt-0.5 px-1 truncate max-w-full">{/(youtube|youtu\.be)/i.test(vid) ? "YouTube" : /vimeo/i.test(vid) ? "Vimeo" : "Video"}</span>
                      </div>
                      <button
                        onClick={() => handleRemoveVideo(idx)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  data-testid="add-video-url-input"
                  placeholder="Paste YouTube / Vimeo / video URL"
                  value={newVideoUrl}
                  onChange={e => setNewVideoUrl(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddVideoUrl(); } }}
                  className="flex-1"
                />
                <Button type="button" variant="outline" size="sm" onClick={handleAddVideoUrl}>Add</Button>
              </div>
              <Input
                type="file"
                accept="video/*"
                onChange={handleVideoUpload}
                className="hidden"
                disabled={uploading}
                ref={videosInputRef}
              />
              <p className="text-xs text-neutral-500">Paste a YouTube/Vimeo or direct video link and click Add. YouTube links play inline on the product page.</p>
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium">Name</Label>
            <Input data-testid="edit-name" value={name} onChange={e => setName(e.target.value)} className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Price</Label>
              <Input data-testid="edit-price" type="number" value={price} onChange={e => setPrice(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-sm font-medium">Stock</Label>
              <Input data-testid="edit-stock" type="number" value={stock} onChange={e => setStock(e.target.value)} className="mt-1" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch data-testid="edit-in-stock" checked={inStock} onCheckedChange={setInStock} />
            <Label className="text-sm">In Stock</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-brand-primary hover:bg-brand-primary-hover text-white" onClick={handleSubmit} disabled={saving} data-testid="save-product-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RFQsTab() {
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRfqs = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/rfq`, { headers: getAuthHeaders() })
      .then(({ data }) => setRfqs(data.rfqs || []))
      .catch(() => toast.error("Failed to load RFQs"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchRfqs(); }, [fetchRfqs]);

  const updateStatus = async (rfqId, newStatus) => {
    try {
      await axios.put(`${API}/rfq/${rfqId}/status`, { status: newStatus }, { headers: getAuthHeaders() });
      setRfqs(prev => prev.map(r => r.id === rfqId ? { ...r, status: newStatus } : r));
      toast.success(`Status updated to ${newStatus}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const deleteRfq = async (rfqId) => {
    if (!window.confirm("Are you sure you want to delete this RFQ?")) return;
    try {
      await axios.delete(`${API}/rfq/${rfqId}`, { headers: getAuthHeaders() });
      setRfqs(prev => prev.filter(r => r.id !== rfqId));
      toast.success("RFQ deleted");
    } catch {
      toast.error("Failed to delete RFQ");
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand-primary" /></div>;

  if (rfqs.length === 0) return (
    <div className="text-center py-16 text-neutral-500">
      <FileText className="h-12 w-12 text-neutral-300 mx-auto mb-3" />
      <p className="font-medium">No RFQs yet</p>
      <p className="text-sm text-neutral-400 mt-1">RFQs submitted via WhatsApp will appear here</p>
    </div>
  );

  return (
    <div className="border rounded-xl overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-neutral-50">
            <TableHead className="font-semibold">Date</TableHead>
            <TableHead className="font-semibold">Customer</TableHead>
            <TableHead className="font-semibold">Phone</TableHead>
            <TableHead className="font-semibold text-center">Items</TableHead>
            <TableHead className="font-semibold">Transit</TableHead>
            <TableHead className="font-semibold text-right">Total</TableHead>
            <TableHead className="font-semibold text-center">Status</TableHead>
            <TableHead className="font-semibold text-center">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rfqs.map(rfq => (
            <TableRow key={rfq.id} data-testid={`rfq-row-${rfq.id}`}>
              <TableCell className="text-sm">{new Date(rfq.created_at).toLocaleDateString('en-IN')}</TableCell>
              <TableCell className="text-sm font-medium">{rfq.customer_name || 'Anonymous'}</TableCell>
              <TableCell className="text-sm">
                {rfq.customer_phone ? (
                  <a href={`tel:${rfq.customer_phone}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                    <Phone className="h-3 w-3" />{rfq.customer_phone}
                  </a>
                ) : '-'}
              </TableCell>
              <TableCell className="text-center">{rfq.items?.length || 0}</TableCell>
              <TableCell><Badge variant="outline" className="text-xs">{rfq.transit_mode}</Badge></TableCell>
              <TableCell className="text-right font-medium">{formatPrice(rfq.total_amount || 0)}</TableCell>
              <TableCell className="text-center">
                <Select value={rfq.status} onValueChange={(val) => updateStatus(rfq.id, val)}>
                  <SelectTrigger className="h-8 w-[120px] mx-auto text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending"><span className="text-amber-600 font-medium">Pending</span></SelectItem>
                    <SelectItem value="quoted"><span className="text-blue-600 font-medium">Quoted</span></SelectItem>
                    <SelectItem value="completed"><span className="text-emerald-600 font-medium">Completed</span></SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell className="text-center">
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => deleteRfq(rfq.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function CategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCategory, setEditCategory] = useState(null);
  const [deleteCategory, setDeleteCategory] = useState(null);
  const [initializing, setInitializing] = useState(false);
  const [imageError, setImageError] = useState(false);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/categories`, { headers: getAuthHeaders() });
      setCategories(data);
    } catch (err) {
      console.error("Error fetching categories:", err);
      toast.error("Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleInitialize = async () => {
    setInitializing(true);
    try {
      const { data } = await axios.post(`${API}/categories/initialize`, {}, { headers: getAuthHeaders() });
      toast.success(data.message);
      fetchCategories();
    } catch (err) {
      console.error("Error initializing categories:", err);
      toast.error("Failed to initialize categories");
    } finally {
      setInitializing(false);
    }
  };

  const handleSaveCategory = async () => {
    if (!editCategory) return;
    try {
      if (editCategory.id) {
        await axios.put(`${API}/categories/${editCategory.id}`, editCategory, { headers: getAuthHeaders() });
        toast.success("Category updated successfully");
      } else {
        await axios.post(`${API}/categories`, editCategory, { headers: getAuthHeaders() });
        toast.success("Category created successfully");
      }
      setEditCategory(null);
      setImageError(false);
      fetchCategories();
    } catch (err) {
      console.error("Error saving category:", err);
      toast.error("Failed to save category");
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  useEffect(() => {
    setImageError(false);
  }, [editCategory?.image_url]);

  const handleDeleteCategory = async () => {
    if (!deleteCategory) return;
    try {
      await axios.delete(`${API}/categories/${deleteCategory.id}`, { headers: getAuthHeaders() });
      toast.success("Category deleted successfully");
      setDeleteCategory(null);
      fetchCategories();
    } catch (err) {
      console.error("Error deleting category:", err);
      toast.error("Failed to delete category");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          <Button onClick={() => setEditCategory({ name: "", image_url: "" })}>
            <ImageIcon className="h-4 w-4 mr-2" /> Add Category
          </Button>
          <Button variant="outline" onClick={handleInitialize} disabled={initializing}>
            {initializing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            Initialize from Default
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand-primary" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">Image</TableHead>
                <TableHead className="font-semibold">Name</TableHead>
                <TableHead className="font-semibold">Product Count</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map(cat => (
                <TableRow key={cat.id || cat.name}>
                  <TableCell>
                    <img src={cat.image_url || "https://via.placeholder.com/50"} alt={cat.name} className="w-12 h-12 rounded-lg object-cover" />
                  </TableCell>
                  <TableCell className="font-medium">{cat.name}</TableCell>
                  <TableCell>{cat.count || 0}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => setEditCategory(cat)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteCategory(cat)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit Category Dialog */}
      <Dialog open={!!editCategory} onOpenChange={() => setEditCategory(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editCategory?.id ? "Edit Category" : "Add Category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Category Name</Label>
              <Input
                value={editCategory?.name || ""}
                onChange={(e) => setEditCategory({ ...editCategory, name: e.target.value })}
                placeholder="Enter category name"
              />
            </div>
            <div>
              <Label>Image URL</Label>
              <Input
                value={editCategory?.image_url || ""}
                onChange={(e) => setEditCategory({ ...editCategory, image_url: e.target.value })}
                placeholder="Enter image URL"
              />
            </div>
            {editCategory?.image_url && (
              <div>
                {imageError ? (
                  <div className="w-full h-32 bg-neutral-100 rounded-lg flex items-center justify-center text-neutral-500 text-sm">
                    Failed to load image. Check the URL.
                  </div>
                ) : (
                  <img
                    src={editCategory.image_url}
                    alt="Preview"
                    className="w-full h-32 object-cover rounded-lg"
                    onError={handleImageError}
                  />
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCategory(null)}>Cancel</Button>
            <Button onClick={handleSaveCategory}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Category Dialog */}
      <Dialog open={!!deleteCategory} onOpenChange={() => setDeleteCategory(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Category</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete "{deleteCategory?.name}"? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteCategory(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteCategory}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
